import Boolean from "@/Components/Boolean/Boolean";
import Button from "@/Components/Button/Button";
import Icon from "@/Components/Icon/Icon";
import Textfield from "@/Components/Input/Textfield";
import Modal from "@/Components/Modal/Modal";
import Select from "@/Components/Select/Select";
import Spinner from "@/Components/Spinner/Spinner";
import useElectronHook from "@/shared/hooks/useElectronHook";
import usePrinterSettingsHook from "@/shared/hooks/usePrinterSettingsHook";
import useFinancialReportReceiptSettingsPut from "@/shared/mutations/financialReport/useFinancialReportReceiptSettingsPut";
import useFinancialReportReceiptSettingsQuery from "@/shared/queries/financialReport/useFinancialReportReceiptSettingsQuery";
import { dayTypeOptions, fontFamilyOptions } from "@/shared/utils/constant";
import { useEffect, useState } from "react";
import { Col, Row } from "react-grid-system";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useImmer } from "use-immer";

interface ReceiptItemPropss {
  label: string | React.ReactNode;
  children: React.ReactNode;
  unit?: string;
}

const ReceiptItem = ({ label, children, unit }: ReceiptItemPropss) => {
  return (
    <Row
      style={{
        alignItems: "center",
        marginBottom: "0.5rem",
      }}
    >
      <Col md={5}>{label}</Col>
      <Col md={7}>
        <div className="flex items-center">
          <div className="w-[95%]">{children}</div>
          {unit && (
            <div
              className="w-[5%] tracking-[-0.8px]"
              style={{ fontWeight: "bold" }}
            >
              {unit}
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
};

const FinancialReportReceiptSettingModal = ({ onExit }: any) => {
  const { t } = useTranslation("");
  const { isElectron } = useElectronHook();
  const { query } = useFinancialReportReceiptSettingsQuery();
  const { update } = useFinancialReportReceiptSettingsPut();
  const { getPrinterSettings } = usePrinterSettingsHook();
  const { register, handleSubmit, errors } = useForm();
  const [printers, setPrinters] = useState<any[]>(null);
  const [fields, setFields] = useImmer<any>({});

  useEffect(() => {
    getPrinterSettings.refetch();
  }, []);

  useEffect(() => {
    if (getPrinterSettings.data) {
      const registeredPrinters = Object.keys(
        getPrinterSettings.data.printers
      ).map((p) => ({ value: p, label: p }));

      setPrinters(registeredPrinters);
    }
  }, [getPrinterSettings.data]);

  useEffect(() => {
    if (query.data) {
      setFields({
        ...query.data,
      });
    }
  }, [query.data]);

  const onSubmit = () => {
    update.mutateAsync(fields, {
      onSuccess: () => {
        onExit();
        query.refetch();
      },
    });
  };

  return (
    <Modal onExit={onExit}>
      <div className="mb-20 mx-20">
        <form onSubmit={handleSubmit(onSubmit)}>
          {query.isPending ? (
            <Spinner />
          ) : printers !== null && !query.isPending && query.isSuccess ? (
            <>
              <h2>{t("financialReport.printSettings")}</h2>
              <div>
                <ReceiptItem label={t("financialReport.automaticPrint")}>
                  <Boolean
                    onChange={(e) =>
                      setFields((draft) => {
                        draft.automatic_print = e.target.checked;
                      })
                    }
                    checked={fields?.automatic_print}
                  />
                </ReceiptItem>
                <ReceiptItem label={t("financialReport.width")} unit="px">
                  <Textfield
                    name="width"
                    onChange={(e) =>
                      setFields((draft) => {
                        draft.width = parseInt(e.target.value);
                      })
                    }
                    type="number"
                    error={errors["width"]?.message}
                    defaultValue={fields?.width}
                    ref={register({
                      required: {
                        value: true,
                        message: t("fieldRequired"),
                      },
                    })}
                  />
                </ReceiptItem>
                <ReceiptItem label={t("financialReport.quantity")}>
                  <Textfield
                    name="print_quantity"
                    onChange={(e) =>
                      setFields((draft) => {
                        draft.print_quantity = e.target.value;
                      })
                    }
                    type="number"
                    error={errors["print_quantity"]?.message}
                    defaultValue={fields?.print_quantity}
                    ref={register({
                      required: {
                        value: true,
                        message: t("fieldRequired"),
                      },
                    })}
                  />
                </ReceiptItem>
                <ReceiptItem label={t("financialReport.marginLeft")} unit="mm">
                  <Textfield
                    name="margin_left"
                    onChange={(e) =>
                      setFields((draft) => {
                        draft.margin_left = parseFloat(e.target.value);
                      })
                    }
                    type="number"
                    error={errors["margin_left"]?.message}
                    defaultValue={fields?.margin_left}
                    ref={register({
                      required: {
                        value: true,
                        message: t("fieldRequired"),
                      },
                    })}
                  />
                </ReceiptItem>
                <ReceiptItem label={t("financialReport.marginTop")} unit="mm">
                  <Textfield
                    name="margin_top"
                    onChange={(e) =>
                      setFields((draft) => {
                        draft.margin_top = parseFloat(e.target.value);
                      })
                    }
                    error={errors["margin_top"]?.message}
                    type="number"
                    defaultValue={fields?.margin_top}
                    ref={register({
                      required: {
                        value: true,
                        message: t("fieldRequired"),
                      },
                    })}
                  />
                </ReceiptItem>
                <ReceiptItem label={t("financialReport.marginRight")} unit="mm">
                  <Textfield
                    name="margin_right"
                    onChange={(e) =>
                      setFields((draft) => {
                        draft.margin_right = parseFloat(e.target.value);
                      })
                    }
                    error={errors["margin_right"]?.message}
                    type="number"
                    defaultValue={fields?.margin_right}
                    ref={register({
                      required: {
                        value: true,
                        message: t("fieldRequired"),
                      },
                    })}
                  />
                </ReceiptItem>
                <ReceiptItem
                  label={t("financialReport.marginBottom")}
                  unit="mm"
                >
                  <Textfield
                    name="margin_bottom"
                    onChange={(e) =>
                      setFields((draft) => {
                        draft.margin_bottom = parseFloat(e.target.value);
                      })
                    }
                    error={errors["margin_bottom"]?.message}
                    type="number"
                    defaultValue={fields?.margin_bottom}
                    ref={register({
                      required: {
                        value: true,
                        message: t("fieldRequired"),
                      },
                    })}
                  />
                </ReceiptItem>

                <ReceiptItem label={t("financialReport.fontSize")} unit="px">
                  <Textfield
                    name="font_size"
                    onChange={(e) =>
                      setFields((draft) => {
                        draft.font_size = e.target.value;
                      })
                    }
                    error={errors["font_size"]?.message}
                    type="number"
                    defaultValue={fields?.font_size}
                    ref={register({
                      required: {
                        value: true,
                        message: t("fieldRequired"),
                      },
                    })}
                  />
                </ReceiptItem>
                <ReceiptItem label={t("financialReport.fontFamily")}>
                  <Select
                    onChange={(v) =>
                      setFields((draft) => {
                        draft.font_family = v.value;
                      })
                    }
                    menuPortalTarget={document.getElementById("root")}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 1000000,
                      }),
                    }}
                    options={fontFamilyOptions}
                    getOptionLabel={(option) =>
                      t(`financialReport.${option.label}`)
                    }
                    value={fontFamilyOptions.find(
                      (i) => i.value === fields?.font_family
                    )}
                  ></Select>
                </ReceiptItem>
                <ReceiptItem
                  label={
                    <div className="flex items-center">
                      <div>{t("financialReport.printer")}</div>
                      {isElectron && (
                        <div
                          onClick={() => {
                            window.api.send("getPrinters", null);
                          }}
                          className="inline cursor-pointer"
                        >
                          <Icon className="ml-8 h-7 w-7" variants="sync" />
                        </div>
                      )}
                    </div>
                  }
                >
                  <Select
                    onChange={(v) =>
                      setFields((draft) => {
                        draft.chosen_printer = v.value;
                      })
                    }
                    menuPortalTarget={document.getElementById("root")}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 1000000,
                      }),
                    }}
                    options={printers}
                    value={printers.find(
                      (i) => i.value === fields?.chosen_printer
                    )}
                  ></Select>
                </ReceiptItem>
                <ReceiptItem label={t("financialReport.dayType")}>
                  <Select
                    onChange={(v) =>
                      setFields((draft) => {
                        draft.day_type = v.value;
                      })
                    }
                    menuPortalTarget={document.getElementById("root")}
                    styles={{
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 1000000,
                      }),
                    }}
                    getOptionLabel={(option) =>
                      `${t(`financialReport.${option.translationKey}`)}`
                    }
                    options={dayTypeOptions}
                    value={dayTypeOptions.find(
                      (i) => i.value === fields?.day_type
                    )}
                  ></Select>
                </ReceiptItem>
              </div>
              <div className="mt-4 flex justify-end">
                <Button isPending={update.isPending}>
                  {t("financialReport.update")}
                </Button>
              </div>
            </>
          ) : null}
        </form>
      </div>
    </Modal>
  );
};

export default FinancialReportReceiptSettingModal;
